import React, { useState, useMemo, useEffect } from "react";
import { CustomModal } from "@/components/Modal";
import { PageTitle } from "@/components/Title";
import { Box, Typography, Button } from "@mui/material";
import {
  type TradePriceGridRow,
  type TradePriceListFilter,
} from "@/features/map/types/tradePrice";
import { AcquireBookDataGrid } from "@/components/DataGrid";
import {
  type GridColDef,
  type GridRenderCellParams,
  type GridColumnGroupingModel,
} from "@mui/x-data-grid-pro";
import FilterListIcon from "@mui/icons-material/FilterList";
import { TradePriceFilterModal } from "./TradePriceFilterModal";
import MapIcon from "@mui/icons-material/Map";
import { usePrefectures } from "@/hooks/usePrefectures";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  rows: TradePriceGridRow[];
  loading: boolean;
  filters: TradePriceListFilter;
  onFilterApply: (filters: TradePriceListFilter) => void;
  onShowOnMap?: (location: string, prefName: string) => void;
  location?: {
    municipality: string;
    districtName: string;
  } | null;
  showSearchCondition?: boolean;
}

export const TradePriceListModal: React.FC<IProps> = ({
  open,
  setOpen,
  rows,
  loading,
  filters,
  onFilterApply,
  onShowOnMap,
  location,
  showSearchCondition = true,
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [localRows, setLocalRows] = useState<TradePriceGridRow[]>(rows);
  const rowsPerPageOptions: number[] = [25, 50, 100];
  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);

  const { prefectures } = usePrefectures();

  // 検索条件の表示用文字列を生成
  const searchConditionText = useMemo(() => {
    const prefectureName =
      filters.prefecture !== null
        ? prefectures.find(
            (prefecture) => Number(prefecture.prefCode) === filters.prefecture
          )?.name ?? ""
        : "";

    const locationText =
      location && (location.municipality || location.districtName)
        ? `${location.municipality}${location.districtName}`
        : filters.location ?? "";

    const prefectureWithLocation = locationText
      ? `${prefectureName}${locationText}`
      : prefectureName;

    const priceClassificationText = filters.priceClassifications
      .map((classification) => {
        if (classification === "realEstateTransaction") {
          return "不動産取引価格情報";
        } else if (classification === "contractPrice") {
          return "成約価格情報";
        }
        return "";
      })
      .filter((text) => text !== "")
      .join("、");

    const yearText = filters.year !== undefined ? `${filters.year}年` : "";

    return `${prefectureWithLocation} / ${priceClassificationText} / ${yearText}`;
  }, [filters, prefectures, location]);

  // rowsが変更されたらlocalRowsも更新
  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);

  const changePage = (newPage: number): void => {
    setPage(newPage);
  };

  const changePageSize = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const columns: Array<GridColDef<TradePriceGridRow>> = useMemo(
    () => [
      {
        field: "mapLink",
        headerName: "",
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (
          params: GridRenderCellParams<unknown, TradePriceGridRow>
        ) => (
          <Button
            size="small"
            variant="text"
            startIcon={<MapIcon />}
            onClick={() => {
              if (onShowOnMap) {
                const location = `${params.row.municipality || ""}${
                  params.row.districtName || ""
                }`;
                onShowOnMap(location, params.row.prefecture || "");
                setOpen(false);
              }
            }}
            disabled={!onShowOnMap}
          >
            近くに飛ぶ
          </Button>
        ),
      },
      {
        field: "type",
        headerName: "種類",
        width: 150,
      },
      {
        field: "priceClassification",
        headerName: "価格情報区分",
        width: 180,
      },
      {
        field: "location",
        headerName: "所在地",
        width: 300,
        valueGetter: (params) => {
          const { prefecture, municipality, districtName } = params.row;
          return `${prefecture || ""}${municipality || ""}${
            districtName || ""
          }`;
        },
      },
      {
        field: "region",
        headerName: "地域",
        width: 150,
      },
      {
        field: "tradePrice",
        headerName: "取引総額",
        width: 150,
        renderCell: ({ value }: GridRenderCellParams<string>) => {
          return value != null && value !== ""
            ? `${Math.round(Number(value) / 10000).toLocaleString()}万円`
            : "";
        },
      },
      {
        field: "pricePerUnit",
        headerName: "坪単価",
        width: 120,
        renderCell: ({ value }: GridRenderCellParams<string>) => {
          return value != null && value !== ""
            ? `${Math.round(Number(value) / 10000).toLocaleString()}万円`
            : "";
        },
      },
      {
        field: "area",
        headerName: "面積",
        width: 120,
        renderCell: ({ value }: GridRenderCellParams<string>) => {
          return value != null && value !== ""
            ? `${Number(value).toLocaleString()}㎡`
            : "";
        },
      },
      {
        field: "unitPrice",
        headerName: "㎡単価",
        width: 120,
        renderCell: ({ value }: GridRenderCellParams<string>) => {
          return value != null && value !== ""
            ? `${Math.round(Number(value) / 10000).toLocaleString()}万円`
            : "";
        },
      },
      {
        field: "landShape",
        headerName: "形状",
        width: 120,
      },
      {
        field: "totalFloorArea",
        headerName: "延床面積",
        width: 120,
        renderCell: ({ value }: GridRenderCellParams<string>) => {
          return value != null && value !== ""
            ? `${Number(value).toLocaleString()}㎡`
            : "";
        },
      },
      {
        field: "buildingYear",
        headerName: "建築年",
        width: 120,
      },
      {
        field: "structure",
        headerName: "構造",
        width: 120,
      },
      {
        field: "use",
        headerName: "用途",
        width: 120,
      },
      {
        field: "purpose",
        headerName: "今後の利用目的",
        width: 150,
      },
      {
        field: "breadth",
        headerName: "幅員",
        width: 100,
        renderCell: ({ value }: GridRenderCellParams<string>) => {
          return value != null && value !== "" ? `${value}m` : "";
        },
      },
      {
        field: "classification",
        headerName: "種類",
        width: 120,
      },
      {
        field: "direction",
        headerName: "方位",
        width: 100,
      },
      {
        field: "cityPlanning",
        headerName: "都市計画",
        width: 150,
      },
      {
        field: "coverageRatio",
        headerName: "建蔽率",
        width: 100,
        renderCell: ({ value }: GridRenderCellParams<string>) => {
          return value != null && value !== "" ? `${value}%` : "";
        },
      },
      {
        field: "floorAreaRatio",
        headerName: "容積率",
        width: 100,
        renderCell: ({ value }: GridRenderCellParams<string>) => {
          return value != null && value !== "" ? `${value}%` : "";
        },
      },
      {
        field: "period",
        headerName: "取引時期",
        width: 150,
      },
      {
        field: "remarks",
        headerName: "取引の事情等",
        width: 200,
      },
    ],
    [onShowOnMap, setOpen]
  );

  const columnGroupingModel: GridColumnGroupingModel = useMemo(
    () => [
      {
        groupId: "basicInfo",
        headerName: "基本情報",
        headerAlign: "center",
        children: [
          { field: "type" },
          { field: "priceClassification" },
          { field: "location" },
          { field: "region" },
          { field: "tradePrice" },
        ],
      },
      {
        groupId: "land",
        headerName: "土地",
        headerAlign: "center",
        children: [
          { field: "pricePerUnit" },
          { field: "area" },
          { field: "unitPrice" },
          { field: "landShape" },
        ],
      },
      {
        groupId: "building",
        headerName: "建物",
        headerAlign: "center",
        children: [
          { field: "totalFloorArea" },
          { field: "buildingYear" },
          { field: "structure" },
          { field: "use" },
        ],
      },
      {
        groupId: "usage",
        headerName: "利用",
        headerAlign: "center",
        children: [{ field: "purpose" }],
      },
      {
        groupId: "frontRoad",
        headerName: "前面道路",
        headerAlign: "center",
        children: [
          { field: "breadth" },
          { field: "classification" },
          { field: "direction" },
        ],
      },
      {
        groupId: "others",
        headerName: "その他",
        headerAlign: "center",
        children: [
          { field: "cityPlanning" },
          { field: "coverageRatio" },
          { field: "floorAreaRatio" },
          { field: "period" },
          { field: "remarks" },
        ],
      },
    ],
    []
  );

  return (
    <CustomModal
      isOpen={open}
      handleClose={(
        _event: Record<string, unknown>,
        reason: "backdropClick" | "escapeKeyDown"
      ): void => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        setOpen(false);
      }}
      ariaLabelledby="trade-price-list-modal"
      ariaDescribedby="trade-price-list-modal-description"
      width={"90%"}
      height={"80%"}
      closeOnBackdropClick={false}
      fullScreenOnMobile
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <PageTitle>取引価格一覧</PageTitle>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setOpen(false);
            }}
          >
            × 閉じる
          </Button>
        </Box>
      </Box>
      {/* 検索条件表示 */}
      {showSearchCondition && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            backgroundColor: "grey.50",
            borderRadius: 1,
            border: 1,
            borderColor: "grey.300",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>検索条件：</strong>
            {searchConditionText}
          </Typography>
        </Box>
      )}
      {/* 検索条件ボタン */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 2,
          alignItems: "center",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterListIcon />}
          onClick={() => {
            setFilterModalOpen(true);
          }}
        >
          検索条件
        </Button>
        {showSearchCondition && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: "auto" }}
          >
            {rows.length.toLocaleString("ja-JP")}件
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          mt: 2,
          height: "80%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <Box sx={{ height: 600, width: "100%" }}>
            <AcquireBookDataGrid
              loading={loading}
              rows={localRows}
              columns={columns}
              columnGroupingModel={columnGroupingModel}
              experimentalFeatures={{ columnGrouping: true }}
              page={page}
              pageSize={pageSize}
              onPageChange={changePage}
              onPageSizeChange={changePageSize}
              rowsPerPageOptions={rowsPerPageOptions}
              disableSelectionOnClick
            />
          </Box>
        </Box>
      </Box>
      <TradePriceFilterModal
        open={filterModalOpen}
        onClose={() => {
          setFilterModalOpen(false);
        }}
        initialFilters={filters}
        onApply={onFilterApply}
      />
    </CustomModal>
  );
};
